import { IModifier } from '@Core/Events/EventModifiers/Modifier';
import { OrientationModifier } from '@Core/Events/EventModifiers/OrientationModifier';
import { filterEventsByOwnModifiers, getEventWithoutParams } from '@Core/Utils/ModifierUtils';
import { Game } from '@Game/Game';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ETurboModeLevels, ISoundsConfiguration, TAvailableSounds } from '@GFTypes/GameStoreTypes';
import EventsCenter, { IEventData } from '@Managers/Events/EventsCenter';
import { ALL_EVENTS_LIST, EGameEvents, isWildcardEvent } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { getCurrentTurboLevel } from '@Managers/TurboManager/TurboManager';
import { Howl } from 'howler';
import BackgroundSoundManager from './BackgroundSoundManager';
import { SoundSettingsManager } from './SoundSettingsManager';
import SymbolSoundsManager from './SymbolSoundsManager';
import WinSoundsManager from './WinSoundsManager';

export class SoundManager {
    public static readonly sounds = new Map<string, Howl>();
    public static readonly pendingSounds = new Map<string, number>();
    private readonly soundIds = new Map<string, number>();
    private readonly game: Game;
    private readonly gameStore: GameStore;
    private readonly backgroundSoundManager: BackgroundSoundManager;
    public readonly winSoundsManager: WinSoundsManager;
    public readonly symbolSoundsManager: SymbolSoundsManager;
    private userMuted = false;
    private focusMuted = false;
    public readonly soundSettingsManager: SoundSettingsManager;
    public soundsConfiguration: ISoundsConfiguration;
    public soundNamesByEvents: Map<string, { play: string[]; stop: string[] }> = new Map();
    private readonly modifiers;

    public static async loadSounds(sounds: TAvailableSounds, basePath: string, onLoaded?: () => void): Promise<void> {
        const promises = Object.entries(sounds).map(([key, config]) => {
            return new Promise<void>((resolve, reject) => {
                const howl = new Howl({
                    src: [`${basePath}/${config.file}`],
                    loop: config.loop
                });
                howl.once('load', () => {
                    onLoaded?.();
                    resolve();
                });
                howl.once('loaderror', (_, error) => reject(error));
                SoundManager.sounds.set(key, howl);
            });
        });
        await Promise.all(promises);
    }

    constructor(game: Game) {
        Logger.log(LoggerLevel.Constructor, {
            class: 'SoundManager',
            message: 'SoundManager constructor',
            source: LoggerSource.Game
        });
        this.game = game;
        this.gameStore = this.game.gameStore;
        this.soundSettingsManager = new SoundSettingsManager(this.game, this);
        this.backgroundSoundManager = new BackgroundSoundManager(this.game, this);
        this.winSoundsManager = new WinSoundsManager(this.game, this);
        this.symbolSoundsManager = new SymbolSoundsManager();
        this.modifiers = this.game.globalEventModifiers.filter((mod) => !(mod instanceof OrientationModifier));
        this.init();
    }

    public init() {
        this.soundsConfiguration = this.game.gameStore.get(EGameStoreKeys.SoundsConfiguration);
        Object.entries(this.soundsConfiguration.sounds).forEach(([soundKey, config]) => {
            config?.onEvents?.forEach((event) => {
                if (!this.soundNamesByEvents.has(event)) this.soundNamesByEvents.set(event, { play: [], stop: [] });
                this.soundNamesByEvents.get(event)!.play.push(soundKey);
            });
            config?.offEvents?.forEach((event) => {
                if (!this.soundNamesByEvents.has(event)) this.soundNamesByEvents.set(event, { play: [], stop: [] });
                this.soundNamesByEvents.get(event)!.stop.push(soundKey);
            });
        });
        EventsCenter.on(EGameEvents.PLAY_SOUND_BY_KEY, (key: string) => this.playSound(key), this);
        EventsCenter.on(EGameEvents.MUTE, () => this.setUserMuted(true), this);
        EventsCenter.on(EGameEvents.UNMUTE, () => this.setUserMuted(false), this);

        Object.keys(ALL_EVENTS_LIST).forEach((event) => {
            EventsCenter.on(
                event,
                (data: IEventData) => {
                    this.stopEventSound(event as keyof typeof ALL_EVENTS_LIST, data.eventIds, data.modifiers);
                    this.playEventSound(event as keyof typeof ALL_EVENTS_LIST, data.eventIds, data.modifiers);
                },
                this
            );
        });

        window.addEventListener('blur', () => {
            this.focusMuted = true;
            this.updateMuteState();
        });

        window.addEventListener('focus', () => {
            this.focusMuted = false;
            this.updateMuteState();
        });

        this.soundSettingsManager.init();
    }

    private setUserMuted(state: boolean): void {
        this.userMuted = state;
        this.updateMuteState();
    }

    private updateMuteState(): void {
        const shouldMute = this.userMuted || this.focusMuted;
        Howler.mute(shouldMute);
    }

    public static getSoundDuration(key: string): number | undefined {
        const sound = SoundManager.sounds.get(key);

        if (!sound) {
            console.warn(`[SoundManager] Sound "${key}" not found`);
            return undefined;
        }

        return sound.duration();
    }

    public playSound(
        key: string,
        options?: {
            rate?: number;
            loop?: boolean;
        }
    ): number | undefined {
        const sound = SoundManager.sounds.get(key);

        if (!sound || SoundManager.pendingSounds.has(key)) return undefined;

        sound.stop();

        const id = sound.play();
        SoundManager.pendingSounds.set(key, id);
        if (options?.rate !== undefined) {
            sound.rate(options.rate, id);
        }

        if (options?.loop !== undefined) {
            sound.loop(options.loop, id);
        }

        this.soundIds.set(key, id);

        sound.once('play', () => SoundManager.pendingSounds.delete(key));

        return id;
    }

    public stopSound(key: string): void {
        const sound = SoundManager.sounds.get(key);
        const id = this.soundIds.get(key);

        if (!sound || id === undefined) return;

        sound.stop(id);
        this.soundIds.delete(key);
    }

    public isPlaying(key: string): boolean {
        const sound = SoundManager.sounds.get(key);
        const id = this.soundIds.get(key);

        return sound !== undefined && id !== undefined && sound.playing(id);
    }

    public pauseSound(key: string): void {
        const sound = SoundManager.sounds.get(key);
        const id = this.soundIds.get(key);

        if (!sound || id === undefined) return;

        sound.pause(id);
    }

    public resumeSound(key: string): void {
        const sound = SoundManager.sounds.get(key);
        const id = this.soundIds.get(key);

        if (!sound || id === undefined || SoundManager.pendingSounds.has(key)) return;

        sound.play(id);
    }

    private getSoundNamesForEvent(event: keyof typeof ALL_EVENTS_LIST, modifiers: IModifier[], playOrStop: 'play' | 'stop', ids: number[] = []): string[] {
        let actualEvent = event as string;
        if (isWildcardEvent(event)) {
            let idIndex = 0;
            actualEvent = actualEvent.replaceAll('$', () => {
                return (ids[idIndex++] ?? '$').toString();
            });
        }
        const soundEvents: string[] = Array.from(this.soundNamesByEvents.keys());
        const possibleSoundEvents = soundEvents.filter((soundEvent) => {
            const candidate = getEventWithoutParams(soundEvent).split('_');
            const actual = actualEvent.split('_');
            if (candidate.length !== actual.length) {
                return false;
            }
            return candidate.every((part, index) => {
                return part === '$' || part === actual[index];
            });
        });
        const finalEventNames = filterEventsByOwnModifiers(possibleSoundEvents, modifiers);
        if (!finalEventNames) return [];
        return finalEventNames.flatMap((finalEventName) => this.soundNamesByEvents.get(finalEventName)?.[playOrStop] ?? []);
    }

    public playEventSound(event: keyof typeof ALL_EVENTS_LIST, ids?: number[], additionalModifiers?: IModifier[]): void {
        Logger.log(LoggerLevel.Function, {
            class: 'SoundManager',
            message: `SoundManager :: playSound for event - ${event} ${ids ?? ''}`,
            source: LoggerSource.Game
        });

        const soundConfig = this.gameStore.get(EGameStoreKeys.SoundsConfiguration).sounds;
        const finalModifiers = additionalModifiers ? [...this.modifiers, ...additionalModifiers] : this.modifiers;
        const soundNames = this.getSoundNamesForEvent(event, finalModifiers, 'play', ids);
        if (!soundNames.length) return;
        const { active } = this.game.gameStore.get(EGameStoreKeys.TurboModeState);
        const level = getCurrentTurboLevel(this.gameStore);
        const isTurbo = active && level === ETurboModeLevels.TURBO;
        for (const soundName of soundNames) {
            const { loop, delay, turboRate } = soundConfig[soundName];
            const sound = SoundManager.sounds.get(soundName);
            if (!sound) return;
            sound.rate(isTurbo && turboRate ? turboRate : 1);
            sound.loop(loop);
            sound.stop();
            sound.play();
        }
    }

    public stopEventSound(event: keyof typeof ALL_EVENTS_LIST, ids?: number[], additionalModifiers?: IModifier[]): void {
        const soundNames = this.getSoundNamesForEvent(event, this.modifiers, 'stop', ids);
        for (const soundName of soundNames) {
            const sound = SoundManager.sounds.get(soundName);
            if (sound?.playing()) {
                sound.stop();
            }
        }
    }

    public isMuted(): boolean {
        return this.userMuted || this.focusMuted;
    }

    // public mute(): void {
    //     this.game.sound.setMute(true);
    // }

    // public unmute(): void {
    //     this.game.sound.setMute(false);
    // }

    public async waitForSoundComplete(key: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const sound = SoundManager.sounds.get(key);

            if (!sound) {
                reject(new Error(`Sound "${sound}" not found`));
                return;
            }

            if (!sound.playing()) {
                resolve();
                return;
            }

            sound.once('end', () => resolve());
        });
    }
}
